/**
 * Navigation Context
 * Provides navigation state management, breadcrumbs, and navigation helpers
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export interface BreadcrumbItem {
  label: string;
  path: string;
  isActive?: boolean;
}

export interface NavigationHistoryItem {
  path: string;
  label: string;
  timestamp: Date;
  state?: any;
}

export interface NavigationContextType {
  breadcrumbs: BreadcrumbItem[];
  addBreadcrumb: (item: BreadcrumbItem) => void;
  removeBreadcrumb: (index: number) => void;
  clearBreadcrumbs: () => void;
  setBreadcrumbs: (items: BreadcrumbItem[]) => void;
  goBack: () => void;
  goToDashboard: () => void;
  navigationHistory: NavigationHistoryItem[];
  selectedManufacturer: string | null;
  setSelectedManufacturer: (manufacturerId: string | null) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

interface NavigationProviderProps {
  children: ReactNode;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [breadcrumbs, setBreadcrumbsState] = useState<BreadcrumbItem[]>([]);
  const [navigationHistory, setNavigationHistory] = useState<NavigationHistoryItem[]>([]);
  const [selectedManufacturer, setSelectedManufacturerState] = useState<string | null>(null);

  // Load state from localStorage on mount
  useEffect(() => {
    try {
      const savedState = localStorage.getItem('navigation-state');
      if (savedState) {
        const parsed = JSON.parse(savedState);
        if (parsed.selectedManufacturer) {
          setSelectedManufacturerState(parsed.selectedManufacturer);
        }
        if (parsed.navigationHistory) {
          setNavigationHistory(parsed.navigationHistory.map((item: any) => ({
            ...item,
            timestamp: new Date(item.timestamp)
          })));
        }
      }
    } catch (error) {
      console.warn('Failed to load navigation state from localStorage:', error);
    }
  }, []);

  // Save state to localStorage when it changes
  useEffect(() => {
    try {
      const stateToSave = {
        selectedManufacturer,
        navigationHistory: navigationHistory.slice(-10) // Keep only last 10 items
      };
      localStorage.setItem('navigation-state', JSON.stringify(stateToSave));
    } catch (error) {
      console.warn('Failed to save navigation state to localStorage:', error);
    }
  }, [selectedManufacturer, navigationHistory]);

  // Update breadcrumbs based on current location
  useEffect(() => {
    const path = location.pathname;
    const newBreadcrumbs: BreadcrumbItem[] = [];

    // Add home breadcrumb based on context
    if (path.startsWith('/admin')) {
      newBreadcrumbs.push({ label: 'Admin Dashboard', path: '/admin' });
    } else if (path.startsWith('/manufacturer')) {
      newBreadcrumbs.push({ label: 'Manufacturer Dashboard', path: '/manufacturer' });
    } else {
      // Default to admin for backward compatibility
      newBreadcrumbs.push({ label: 'Admin Dashboard', path: '/admin' });
    }

    // Parse path and add breadcrumbs
    if (path.includes('/admin/credit-management')) {
      newBreadcrumbs.push({ label: 'Credit Management', path: '/admin/credit-management' });
    } else if (path.includes('/admin/manufacturers')) {
      newBreadcrumbs.push({ label: 'Manufacturers', path: '/admin/manufacturers' });
      
      // Check if viewing specific manufacturer
      const manufacturerIdMatch = path.match(/\/admin\/manufacturers\/([^\/]+)/);
      if (manufacturerIdMatch) {
        newBreadcrumbs.push({ 
          label: 'Manufacturer Details', 
          path: path,
          isActive: true 
        });
      }
    } else if (path.includes('/admin/analytics')) {
      newBreadcrumbs.push({ label: 'Analytics', path: '/admin/analytics' });
    }

    // Mark last breadcrumb as active
    if (newBreadcrumbs.length > 0) {
      newBreadcrumbs[newBreadcrumbs.length - 1].isActive = true;
    }

    setBreadcrumbsState(newBreadcrumbs);

    // Add to navigation history
    const historyItem: NavigationHistoryItem = {
      path,
      label: newBreadcrumbs[newBreadcrumbs.length - 1]?.label || 'Unknown Page',
      timestamp: new Date()
    };

    setNavigationHistory(prev => {
      // Don't add duplicate consecutive entries
      if (prev.length > 0 && prev[prev.length - 1].path === path) {
        return prev;
      }
      return [...prev.slice(-9), historyItem]; // Keep last 10 items
    });
  }, [location.pathname]);

  const addBreadcrumb = (item: BreadcrumbItem) => {
    setBreadcrumbsState(prev => [...prev, item]);
  };

  const removeBreadcrumb = (index: number) => {
    setBreadcrumbsState(prev => prev.filter((_, i) => i !== index));
  };

  const clearBreadcrumbs = () => {
    setBreadcrumbsState([]);
  };

  const setBreadcrumbs = (items: BreadcrumbItem[]) => {
    setBreadcrumbsState(items);
  };

  const goBack = () => {
    if (navigationHistory.length > 1) {
      const previousPage = navigationHistory[navigationHistory.length - 2];
      navigate(previousPage.path);
    } else {
      navigate(-1);
    }
  };

  const goToDashboard = () => {
    navigate('/admin');
  };

  const setSelectedManufacturer = (manufacturerId: string | null) => {
    setSelectedManufacturerState(manufacturerId);
    
    // Update URL parameters to maintain state
    const searchParams = new URLSearchParams(location.search);
    if (manufacturerId) {
      searchParams.set('manufacturerId', manufacturerId);
    } else {
      searchParams.delete('manufacturerId');
    }
    
    const newSearch = searchParams.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    
    // Update URL without triggering navigation
    window.history.replaceState(null, '', newUrl);
  };

  // Restore manufacturer selection from URL on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const manufacturerIdFromUrl = searchParams.get('manufacturerId');
    if (manufacturerIdFromUrl && manufacturerIdFromUrl !== selectedManufacturer) {
      setSelectedManufacturerState(manufacturerIdFromUrl);
    }
  }, [location.search]);

  const value: NavigationContextType = {
    breadcrumbs,
    addBreadcrumb,
    removeBreadcrumb,
    clearBreadcrumbs,
    setBreadcrumbs,
    goBack,
    goToDashboard,
    navigationHistory,
    selectedManufacturer,
    setSelectedManufacturer
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};